import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    HStack,
    Input,
    PinInput,
    PinInputField,
    Text,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { validatorsForm } from 'components/utils/validatorsForm'
import { AuthRequestConfirmDTO } from 'models'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { useAuthContext } from 'contexts/auth'
import Auth from 'services/auth'
import * as yup from 'yup'
import { DataUser } from '../SignIn/interface'

interface Props {
    dataUser?: DataUser | undefined
}

export const MFA: React.FC<Props> = ({ dataUser }) => {
    const navigate = useNavigate()
    const { id = '' } = useParams()
    const { setCurrentUser } = useAuthContext()
    const [resendCode, setResendCode] = useState<boolean>(false)
    const toast = useToast()

    const schema = yup
        .object({
            codigoConfirmacao: yup
                .string()
                .required(validatorsForm.codigoConfirmacao.required)
        })
        .required()

    const {
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm<AuthRequestConfirmDTO>({
        resolver: yupResolver(schema)
    })

    const setTokens = (tokens: any) => {
        Object.entries(tokens).forEach(([key, value]) => {
            sessionStorage.setItem(key, String(value))
        })

        const newDateToRefreshToken = String(new Date())
        sessionStorage.setItem('date-generate-token', newDateToRefreshToken)
        setCurrentUser(tokens)
    }

    const onSubmit = async ({ codigoConfirmacao }: AuthRequestConfirmDTO) => {
        try {
            const { data: tokens } = await Auth.mfaConfirm({
                nomeUsuario: dataUser?.login,
                session: dataUser?.session,
                code: codigoConfirmacao
            })

            setTokens(tokens)
            navigate('/contratos')
        } catch (error) {
            toast({
                title: 'Falha no login!',
                description:
                    'Não foi possível realizar o login. Verifique o código e tente novamente.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        }
    }

    const onResendCode = async () => {
        try {
            setResendCode(true)
            await Auth.mfaResendCode({
                userName: dataUser?.login
            })

            toast({
                title: 'Código reenviado com sucesso!',
                description:
                    'Enviamos um novo código por SMS. Verifique o celular cadastrado.',
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Falha ao reenviar código!',
                description:
                    'Não foi possível reenviar o código, tente novamente.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        }
        finally {
            setResendCode(false)
        }
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            h="100vh"
            w="100%"
        >
            <Box display="grid" w="100%" maxW="xl" gap={8}>
                <Box>
                    <Logo maxW={187} />
                </Box>
                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading size="md" textAlign="center" mb="2">
                        Insira o código
                    </Heading>
                    <Text textAlign="center" marginX={'24px'}>
                        Enviamos um código por SMS para garantir a segurança do
                        seu login.
                    </Text>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout type="container" full px={6} mt={8}>
                            <Layout type="row" gutter={{ y: 3 }}>
                                <Layout type="col" layout={{ base: 12 }}>
                                    <FormControl
                                        isInvalid={!!errors.codigoConfirmacao}
                                        isRequired
                                    >
                                        <FormLabel
                                            htmlFor="codigoConfirmacao"
                                            mx={0}
                                            textAlign="center"
                                            requiredIndicator={<></>}
                                        >
                                            Código de segurança
                                        </FormLabel>

                                        <HStack
                                            justifyContent="center"
                                            spacing="16px"
                                        >
                                            <PinInput
                                                placeholder="_"
                                                onChange={value =>
                                                    setValue(
                                                        'codigoConfirmacao',
                                                        value
                                                    )
                                                }
                                                defaultValue=""
                                            >
                                                {Array.from({ length: 6 }).map(
                                                    (_, i) => (
                                                        <PinInputField
                                                            key={i}
                                                            data-testid={`Pin_${i}`}
                                                        />
                                                    )
                                                )}
                                            </PinInput>
                                            <Input
                                                hidden
                                                {...register(
                                                    'codigoConfirmacao'
                                                )}
                                                required={false}
                                            />
                                        </HStack>
                                        <FormErrorMessage justifyContent="center">
                                            {errors &&
                                                errors.codigoConfirmacao
                                                    ?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Layout>

                                <Box
                                    display={'flex'}
                                    gap={'8px'}
                                    justifyContent={'center'}
                                >
                                    <Button
                                        size="md"
                                        type="submit"
                                        isLoading={resendCode}
                                        onClick={onResendCode}
                                    >
                                        Reenviar código
                                    </Button>

                                    <Button
                                        colorScheme="primary"
                                        size="md"
                                        type="submit"
                                        isLoading={isSubmitting}
                                        data-testid="btn-submit"
                                    >
                                        Confirmar
                                    </Button>
                                </Box>
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}
